import React from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';

export default function Security() {
  return (
    <>
      <PageHero
        eyebrow="Security"
        title="Built for the most sensitive"
        emphasis="conversation a business has."
        lede="OfficerCapital exists because owners trust the platform to protect what they're not ready to share publicly. That trust isn't earned by claims — it's earned by architecture. Here's how the system is built and how we operate it."
      />

      {/* THE PRINCIPLES */}
      <section className="oc-section">
        <div className="oc-shell">
          <div className="oc-grid-1-2 oc-mb-5">
            <div>
              <div className="oc-eyebrow-gray oc-mb-2">Principles</div>
              <h2 className="oc-h2">
                Three commitments that drive <em className="oc-em">every choice</em>.
              </h2>
            </div>
            <p className="oc-lede oc-max-520" style={{ paddingTop: 14, fontSize: 16 }}>
              Every feature, every dependency, every operational decision gets weighed against these three.
              When something fails the test, we don't ship it.
            </p>
          </div>

          <div className="oc-grid-3">
            <PrincipleCard
              num="01"
              title="Default to closed"
              body="Sensitive data is hidden by default and revealed only on explicit, recorded approval. We don't add convenience features that bypass owner consent."
            />
            <PrincipleCard
              num="02"
              title="Audit everything"
              body="Every access event — who, what, when, from where — is logged and visible to the owner whose data was touched. If you can't audit it, we don't do it."
            />
            <PrincipleCard
              num="03"
              title="Least privilege"
              body="No employee, contractor, or service has access broader than their job requires. Access is granted by role, time-bound where possible, and reviewed."
            />
          </div>
        </div>
      </section>

      {/* DATA PROTECTION */}
      <section className="oc-section oc-section--white">
        <div className="oc-shell">
          <div className="oc-grid-2" style={{ alignItems: 'start' }}>
            <div>
              <div className="oc-eyebrow-gray oc-mb-2">Data protection</div>
              <h2 className="oc-h2">
                Encrypted in transit. Encrypted at <em className="oc-em">rest</em>.
              </h2>
              <p className="oc-body oc-mt-3 oc-max-440">
                All connections to OfficerCapital use modern TLS with up-to-date cipher suites. Listing data, financial
                attachments, messages, and audit records are encrypted at rest using industry-standard symmetric
                encryption with managed key rotation. Database backups are encrypted with separate keys and stored in
                geographically isolated locations.
              </p>
            </div>
            <div className="oc-border-l">
              <div className="oc-stack">
                <DetailRow
                  label="In transit"
                  body="TLS 1.2 minimum, TLS 1.3 preferred. HSTS enforced. Modern cipher suites only — older ciphers are explicitly disabled."
                />
                <hr className="oc-rule" />
                <DetailRow
                  label="At rest"
                  body="AES-256 encryption for all stored data. Managed key rotation. Encrypted backups, geographically isolated."
                />
                <hr className="oc-rule" />
                <DetailRow
                  label="In transit between services"
                  body="Internal service-to-service communication runs over an isolated private network with mutual authentication."
                />
                <hr className="oc-rule" />
                <DetailRow
                  label="Documents"
                  body="Listing attachments are stored in encrypted object storage. Document URLs are time-bounded, single-use, and require an authenticated, authorized session to redeem."
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ACCESS CONTROLS */}
      <section className="oc-section">
        <div className="oc-shell">
          <div className="oc-grid-1-2 oc-mb-5">
            <div>
              <div className="oc-eyebrow-gray oc-mb-2">Access controls</div>
              <h2 className="oc-h2">
                Three layers, one <em className="oc-em">consistent rule</em>.
              </h2>
            </div>
            <p className="oc-lede oc-max-520" style={{ paddingTop: 14, fontSize: 16 }}>
              The platform's discretion model is enforced at every layer — at the database, at the API, in the user
              interface. Each request to read sensitive data is checked against the owner's explicit approval list
              before the data is returned.
            </p>
          </div>

          <div className="oc-grid-3">
            <LayerCard
              num="01"
              title="Public listing layer"
              body="Anonymized headline metrics. Visible to any vetted, subscribed capital partner. Never includes identifying details."
            />
            <LayerCard
              num="02"
              title="Unlocked file layer"
              body="Full financials, customer mix detail, contract structure. Requires both: the partner paid the unlock fee AND the owner approved that specific partner."
            />
            <LayerCard
              num="03"
              title="Direct contact layer"
              body="Identity, customer names, exact contracts. Shared at the owner's discretion through messaging or off-platform."
            />
          </div>

          <div className="oc-card oc-mt-4" style={{ background: 'var(--oc-paper)' }}>
            <div className="oc-row" style={{ gap: 14, alignItems: 'flex-start' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--oc-green-deep)" strokeWidth="1.5" style={{ flexShrink: 0, marginTop: 2 }}>
                <path d="M5 12 L19 12" />
                <path d="M12 5 L19 12 L12 19" />
              </svg>
              <div>
                <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--oc-ink)', marginBottom: 4 }}>
                  Approval is per-partner, not per-listing
                </div>
                <div style={{ fontSize: 14, color: 'var(--oc-body)', lineHeight: 1.6 }}>
                  Approving one capital partner reveals your data to that partner only. The same listing remains
                  anonymized to all other partners. There is no "open this to everyone" option, by design.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AUTH */}
      <section className="oc-section oc-section--white">
        <div className="oc-shell">
          <div className="oc-grid-2" style={{ alignItems: 'start' }}>
            <div>
              <div className="oc-eyebrow-gray oc-mb-2">Authentication</div>
              <h2 className="oc-h2">
                How we know it's <em className="oc-em">you</em>.
              </h2>
              <p className="oc-body oc-mt-3 oc-max-440">
                Passwords are stored as salted, slow hashes — never in plain text and never recoverable, even by us.
                Sessions use short-lived signed tokens that are invalidated on sign-out, password change, or suspicious
                activity. We rate-limit authentication attempts to prevent brute-force attacks. Suspicious sign-in
                patterns trigger an email notification to your account.
              </p>
            </div>
            <div className="oc-border-l">
              <div className="oc-stack">
                <DetailRow
                  label="Password storage"
                  body="Hashed with a modern, slow hashing algorithm (bcrypt-class). Never stored or logged in plain text. Never recoverable, only resettable."
                />
                <hr className="oc-rule" />
                <DetailRow
                  label="Session management"
                  body="Short-lived signed session tokens. Invalidated on sign-out, password change, or suspicious activity."
                />
                <hr className="oc-rule" />
                <DetailRow
                  label="Brute-force protection"
                  body="Failed authentication attempts are rate-limited per account and per source IP. Repeated failures trigger account lockout and notification."
                />
                <hr className="oc-rule" />
                <DetailRow
                  label="Sign-in with Google"
                  body="Available for owner accounts only. Validates the OAuth assertion against Google directly on every sign-in."
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AUDIT */}
      <section className="oc-section">
        <div className="oc-shell">
          <div className="oc-grid-2" style={{ alignItems: 'start' }}>
            <div>
              <div className="oc-eyebrow-gray oc-mb-2">Audit logging</div>
              <h2 className="oc-h2">
                Every access event, <em className="oc-em">on the record</em>.
              </h2>
              <p className="oc-body oc-mt-3 oc-max-440">
                When a capital partner unlocks your listing, views a document, or sends a message, the event is written
                to an append-only audit log with the actor's identity, the resource accessed, the timestamp, and the
                source IP. Owners see their listing's audit log in their dashboard. Records are retained beyond account
                closure to support investigations.
              </p>
            </div>
            <div className="oc-border-l">
              <div className="oc-stack">
                <DetailRow
                  label="What we log"
                  body="Sign-ins, listing views, unlock events, document views, message sends, access grants and revocations, and all administrative actions."
                />
                <hr className="oc-rule" />
                <DetailRow
                  label="Append-only"
                  body="Audit records cannot be modified or deleted by users — including platform administrators — without leaving a separate, higher-privileged record."
                />
                <hr className="oc-rule" />
                <DetailRow
                  label="Owner visibility"
                  body="Owners see access events for their own listings in their dashboard, with partner identity, time, and source."
                />
                <hr className="oc-rule" />
                <DetailRow
                  label="Retention"
                  body="Audit logs are retained for the life of the account and for two years after account closure to support dispute resolution."
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PAYMENTS */}
      <section className="oc-section oc-section--white">
        <div className="oc-shell">
          <div className="oc-grid-2" style={{ alignItems: 'start' }}>
            <div>
              <div className="oc-eyebrow-gray oc-mb-2">Payments</div>
              <h2 className="oc-h2">
                Card data never <em className="oc-em">touches our servers</em>.
              </h2>
              <p className="oc-body oc-mt-3 oc-max-440">
                Subscription fees and unlock fees are processed by a PCI-DSS-compliant payment provider. We receive a
                tokenized reference for billing, not your card number. Full card details, CVV, and bank routing
                information are never stored on OfficerCapital's systems.
              </p>
            </div>
            <div className="oc-border-l">
              <div className="oc-stack">
                <DetailRow
                  label="Tokenization"
                  body="Sensitive payment data is tokenized by the payment provider before it ever reaches our servers. We store a reference token, not the underlying details."
                />
                <hr className="oc-rule" />
                <DetailRow
                  label="Compliance"
                  body="Our payment provider is PCI-DSS Level 1 compliant. By limiting payment data to the provider's environment, we substantially reduce platform exposure."
                />
                <hr className="oc-rule" />
                <DetailRow
                  label="Webhooks"
                  body="Payment event webhooks are signature-verified before being processed. Replay attempts are detected and rejected."
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INFRA & OPERATIONS */}
      <section className="oc-section">
        <div className="oc-shell">
          <div className="oc-grid-1-2 oc-mb-5">
            <div>
              <div className="oc-eyebrow-gray oc-mb-2">Infrastructure &amp; operations</div>
              <h2 className="oc-h2">
                How the system <em className="oc-em">runs</em>.
              </h2>
            </div>
            <p className="oc-lede oc-max-520" style={{ paddingTop: 14, fontSize: 16 }}>
              The boring parts of security — the deployment pipeline, the dependency tracking, the access reviews —
              are where most breaches actually start. Here's how we keep them tight.
            </p>
          </div>

          <div className="oc-grid-3">
            <PracticeCard
              label="Deployment pipeline"
              body="Code changes flow through automated tests and a security audit step before reaching production. No code is deployed directly from a developer's machine."
            />
            <PracticeCard
              label="Dependency monitoring"
              body="Third-party libraries are continuously monitored for known vulnerabilities. Security patches for high and critical issues are applied on an expedited timeline."
            />
            <PracticeCard
              label="Access review"
              body="Internal access to production systems is reviewed regularly. Departing personnel lose access immediately. Service accounts are scoped and rotated."
            />
            <PracticeCard
              label="Backups"
              body="Database backups run on a defined schedule, are encrypted, and are stored in a separate environment. Restoration is tested periodically."
            />
            <PracticeCard
              label="Network isolation"
              body="The database is not directly reachable from the public internet. All traffic is routed through authenticated, application-layer paths with input validation."
            />
            <PracticeCard
              label="Monitoring"
              body="Platform errors, anomalous access patterns, and authentication failures are monitored. On-call response is defined for incidents that may affect user data."
            />
          </div>
        </div>
      </section>

      {/* RESPONSIBLE DISCLOSURE */}
      <section className="oc-section oc-section--white">
        <div className="oc-shell">
          <div className="oc-grid-2" style={{ alignItems: 'start' }}>
            <div>
              <div className="oc-eyebrow-gray oc-mb-2">Responsible disclosure</div>
              <h2 className="oc-h2">
                Found something? <em className="oc-em">Tell us first</em>.
              </h2>
              <p className="oc-body oc-mt-3 oc-max-440">
                If you believe you've found a security vulnerability, we want to hear about it. We commit to acknowledging
                your report within two business days, providing a remediation plan within ten, and crediting you publicly
                if you wish.
              </p>
              <p className="oc-body oc-mt-3 oc-max-440">
                In return, we ask that you do not access data beyond what's necessary to demonstrate the issue, do not
                disclose publicly until we've had a reasonable opportunity to remediate, and do not use the issue to
                harm users.
              </p>
              <div className="oc-mt-4 oc-row" style={{ gap: 8 }}>
                <a href="mailto:security@officercapital.com" className="oc-btn oc-btn--primary">
                  security@officercapital.com
                </a>
              </div>
            </div>
            <div className="oc-border-l">
              <div className="oc-stack">
                <ListPoint title="Acknowledgment" body="Within two business days of your report." />
                <hr className="oc-rule" />
                <ListPoint title="Remediation plan" body="Within ten business days, with realistic timelines." />
                <hr className="oc-rule" />
                <ListPoint title="No legal threats" body="We won't pursue legal action against good-faith researchers acting under this policy." />
                <hr className="oc-rule" />
                <ListPoint title="Recognition" body="We credit researchers who want it. Anonymous reports welcome." />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER LINKS */}
      <section className="oc-section oc-section--tight oc-section--no-border">
        <div className="oc-shell">
          <div className="oc-row-sb" style={{ gap: 32, flexWrap: 'wrap' }}>
            <div>
              <div className="oc-eyebrow-gray oc-mb-2">Related</div>
              <div className="oc-h3" style={{ fontSize: 22, letterSpacing: '-0.01em' }}>
                Read the <Link to="/privacy" style={{ color: 'var(--oc-green-deep)' }}>Privacy Policy</Link>{' '}
                and <Link to="/terms" style={{ color: 'var(--oc-green-deep)' }}>Terms of Service</Link>.
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function PrincipleCard({ num, title, body }) {
  return (
    <div className="oc-card">
      <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', marginBottom: 16 }}>
        <span className="oc-step__num">{num}</span>
        <hr className="oc-rule" style={{ flex: 1, marginTop: 12 }} />
      </div>
      <h3 className="oc-h4" style={{ marginBottom: 8 }}>{title}</h3>
      <p className="oc-body-sm">{body}</p>
    </div>
  );
}

function LayerCard({ num, title, body }) {
  return (
    <div className="oc-card">
      <div className="oc-eyebrow oc-mb-2">Layer {num}</div>
      <h3 className="oc-h4" style={{ marginBottom: 8 }}>{title}</h3>
      <p className="oc-body-sm">{body}</p>
    </div>
  );
}

function PracticeCard({ label, body }) {
  return (
    <div className="oc-card">
      <div className="oc-eyebrow-gray oc-mb-2">{label}</div>
      <p className="oc-body-sm" style={{ fontSize: 13, lineHeight: 1.55 }}>{body}</p>
    </div>
  );
}

function DetailRow({ label, body }) {
  return (
    <div>
      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--oc-ink)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 13, color: 'var(--oc-gray)', lineHeight: 1.55 }}>{body}</div>
    </div>
  );
}

function ListPoint({ title, body }) {
  return (
    <div>
      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--oc-ink)', marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 13, color: 'var(--oc-gray)', lineHeight: 1.55 }}>{body}</div>
    </div>
  );
}
